import { WidgetException } from '../errors/widgetexception.js';
import { i18n } from '../i18n.js';
import IWidget from './iwidget.js';

/**
 * Widget class
 * pseudo interface for implementing widgets
 *
 */
export abstract class Widget implements IWidget {
    /** control element with `[data-widget]` (inside widget template) */
    public $node: HTMLElement;

    /** actual form element (inside widget template) */
    public $input: HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement;

    /** an optional container around `$input`. defaults to `$input`. */
    public $inputContainer: HTMLElement;

    /** the field container which contains the whole row (including field label and body) and `[data-field-name]` */
    public $field: HTMLElement;

    /** not used yet... idea was to have all error messages at the same place iirc */
    public $helps: HTMLElement;

    /** if the field can be empty or not */
    public required: boolean;

    /** save copy required value for showIf */
    public originalRequiredValue: boolean;

    /** the field language */
    public lang = '';

    /** the field name */
    public name = '';

    /** condition for display, e.g. `teaser_active == true` */
    public showIf: string;

    /**
     * class constructor
     * @param $node - widget .field
     */
    constructor($node: HTMLElement, args: Record<string, unknown>) {
        this.$node = $node;
        this.$input = $node.querySelector('input, select, textarea');
        this.$inputContainer = $node.querySelector('.input, .select, .textarea');
        this.required = (typeof $node.dataset.required !== 'undefined');
        this.originalRequiredValue = this.required;
        this.$helps = $node.parentNode.querySelector('.help');
        this.$field = $node.closest('[data-field-name]');
        this.showIf = this.$field?.dataset.showIf;
        this.name = this.$field?.dataset.field;

        if (this.$inputContainer === null) this.$inputContainer = this.$input;

        Object.assign(this, args);
    }

    /**
     * display an information text
     */
    displayError(error: string, severity?: string, ...subs: string[]): void {
        const errorMsg = i18n.has_translation(error) ? i18n.t(error, ...subs) : error;
        let severityClass = 'is-danger';

        if (severity !== null && typeof severity !== 'undefined') {
            severityClass = `is-${severity}`;
        }

        // mark input field
        this.$inputContainer.classList.add(severityClass);

        // add help container
        if (this.$node.parentNode.querySelector('.help-container') === null) {
            (this.$node.parentNode as HTMLElement).insertAdjacentHTML('beforeend', '<div class="help-container"></div>');
        }

        // add help text
        const $helpContainer = this.$node.parentNode.querySelector('.help-container');
        $helpContainer.insertAdjacentHTML('beforeend', `<p class="help ${severityClass}">${errorMsg}</p>`);

        // throw Error
        throw new WidgetException(this, errorMsg, severity);
    }

    /**
     * clear all widget errors and warnings
     */
    clearError(): Widget {
        const classesToRemove = ['is-danger', 'is-warning', 'is-info', 'is-success'];

        // remove error classes from input container
        this.$inputContainer.classList.remove(...classesToRemove);

        this.$helps = this.$node.parentNode.querySelector('.help-container'); // refresh $helps

        if (this.$helps !== null) {
            this.$helps.remove();
        }
        return this;
    }

    /**
     * validates the widget.
     */
    async validate(): Promise<boolean> {
        return true;
    }

    /**
     * is called by the frontend form if some validations failed (front- or backend)
     * you can use this to e.g. reload a widget or reset it.
     */
    async refresh(): Promise<void> {
        // by default no logic
    }

    /**
     */
    async preUploadTask(): Promise<boolean> {
        return true;
    }

    /**
     * getValue stub
     */
    async getValue(): Promise<any> {
        throw new Error('You have to implement the method getValue!');
    }

    /**
     * default event listener
     */
    listen(action: string, fn: (e: Event) => void): void {
        if (this.$input !== null && typeof this.$input.addEventListener === 'function') {
            this.$input.addEventListener(action, fn);
        }
    }

    /**
     * get the widget's field name for storage
     * @returns string
     */
    getStorageName(): string {
        if (this.lang === '') {
            return this.$node.dataset.widgetName;
        }
        // this is really ugly
        return this.$node.dataset.widgetName.split('[').pop().split(']').shift();
    }
}
